import { createSelector } from 'reselect';
import { IJewishness, IJewishnessState } from 'types/jewishness.types';
import { IJewishnessFormData } from 'views/public/JewishnessView/components/JewishnessForm/types';

const getItems = (state: { jewishness: IJewishnessState }) => state.jewishness.items;
const getSelectedItem = (state: { jewishness: IJewishnessState }) => state.jewishness.selectedItem;
const getCementeries = (state: { jewishness: IJewishnessState }) => state.jewishness.cementeries;
const getCivilStates = (state: { jewishness: IJewishnessState }) => state.jewishness.civilStates;
const getIdCardTypes = (state: { jewishness: IJewishnessState }) => state.jewishness.idCardTypes;
const getTribes = (state: { jewishness: IJewishnessState }) => state.jewishness.tribes;

export const jewishnessSelector = createSelector([getItems], (jewishness) =>
  jewishness.map((item: IJewishness) => ({
    ...item,
    fullName: `${item.person.lastName || ''} ${item.person.firstName || ''} y ${
      item.spouse.lastName || ''
    } ${item.spouse.firstName || ''}`,
  })),
);

export const jewishnessFormSelector = createSelector(
  [getSelectedItem],
  (jewishness): IJewishnessFormData => ({
    person: {
      id: jewishness?.person?.id || 0,
      firstName: jewishness?.person?.firstName || '',
      lastName: jewishness?.person?.lastName || '',
      idCardType: jewishness?.person?.idCardType,
      idCard: jewishness?.person?.idCard || '',
      age: jewishness?.person?.age || '',
      country: jewishness?.person?.country || '',
      civilState: jewishness?.person?.civilState || '',
      birthdate: jewishness?.person?.birthdate || '',
      areaCode: jewishness?.person?.areaCode || '',
      phone: jewishness?.person?.phone || '',
      email: jewishness?.person?.email || '',
      community: jewishness?.person?.community || '',
      tribe: jewishness?.person?.tribe || '',
      alive: jewishness?.person?.alive ?? true,
      cementery: jewishness?.person?.cementery || '',
      weddingPlace: jewishness?.person?.weddingPlace || '',
      parents: {
        father: {
          firstName: jewishness?.person?.parents?.father?.firstName || '',
          lastName: jewishness?.person?.parents?.father?.lastName || '',
          idCardType: jewishness?.person?.parents?.father?.idCardType,
          idCard: jewishness?.person?.parents?.father?.idCard || '',
          birthdate: jewishness?.person?.parents?.father?.birthdate || '',
        },
        mother: {
          firstName: jewishness?.person?.parents?.mother?.firstName || '',
          lastName: jewishness?.person?.parents?.mother?.lastName || '',
          idCardType: jewishness?.person?.parents?.mother?.idCardType,
          idCard: jewishness?.person?.parents?.mother?.idCard || '',
          birthdate: jewishness?.person?.parents?.mother?.birthdate || '',
        },
        weddingPlace: jewishness?.person?.parents?.weddingPlace || '',
        weddingDate: jewishness?.person?.parents?.weddingDate || '',
      },
      grandParents: {
        paternalFather: {
          firstName: jewishness?.person?.grandParents?.paternalFather?.firstName || '',
          lastName: jewishness?.person?.grandParents?.paternalFather?.lastName || '',
          birthplace: jewishness?.person?.grandParents?.paternalFather?.birthplace || '',
        },
        paternalMother: {
          firstName: jewishness?.person?.grandParents?.paternalMother?.firstName || '',
          lastName: jewishness?.person?.grandParents?.paternalMother?.lastName || '',
          birthplace: jewishness?.person?.grandParents?.paternalMother?.birthplace || '',
        },
        maternalFather: {
          firstName: jewishness?.person?.grandParents?.maternalFather?.firstName || '',
          lastName: jewishness?.person?.grandParents?.maternalFather?.lastName || '',
          birthplace: jewishness?.person?.grandParents?.maternalFather?.birthplace || '',
        },
        maternalMother: {
          firstName: jewishness?.person?.grandParents?.maternalMother?.firstName || '',
          lastName: jewishness?.person?.grandParents?.maternalMother?.lastName || '',
          birthplace: jewishness?.person?.grandParents?.maternalMother?.birthplace || '',
        },
      },
      witnesses: [
        {
          name: jewishness?.person?.witnesses?.[0]?.name || '',
          idCardType: jewishness?.person?.witnesses?.[0]?.idCardType,
          idCard: jewishness?.person?.witnesses?.[0]?.idCard || '',
          address: jewishness?.person?.witnesses?.[0]?.address || '',
          areaCode: jewishness?.person?.witnesses?.[0]?.areaCode || '',
          phone: jewishness?.person?.witnesses?.[0]?.phone || '',
        },
        {
          name: jewishness?.person?.witnesses?.[1]?.name || '',
          idCardType: jewishness?.person?.witnesses?.[1]?.idCardType,
          idCard: jewishness?.person?.witnesses?.[1]?.idCard || '',
          address: jewishness?.person?.witnesses?.[1]?.address || '',
          areaCode: jewishness?.person?.witnesses?.[1]?.areaCode || '',
          phone: jewishness?.person?.witnesses?.[1]?.phone || '',
        },
      ],
    },
    spouse: {
      id: jewishness?.spouse?.id || 0,
      firstName: jewishness?.spouse?.firstName || '',
      lastName: jewishness?.spouse?.lastName || '',
      idCardType: jewishness?.spouse?.idCardType,
      idCard: jewishness?.spouse?.idCard || '',
      country: jewishness?.spouse?.country || '',
      birthdate: jewishness?.spouse?.birthdate || '',
      email: jewishness?.spouse?.email || '',
      community: jewishness?.spouse?.community || '',
      tribe: jewishness?.spouse?.tribe || '',
      alive: jewishness?.spouse?.alive ?? true,
      cementery: jewishness?.spouse?.cementery || '',
    },
    comments: jewishness?.comments || '',
  }),
);

export const jewishnessCementeriesSelector = createSelector([getCementeries], (cementeries) =>
  Object.keys(cementeries).map((key) => ({
    label: cementeries[key],
    value: key,
  })),
);

export const jewishnessCivilStatesSelector = createSelector([getCivilStates], (civilStates) =>
  Object.keys(civilStates).map((key) => ({
    label: civilStates[key],
    value: key,
  })),
);

export const jewishnessIdCardTypesSelector = createSelector([getIdCardTypes], (idCardTypes) =>
  Object.keys(idCardTypes).map((key) => ({
    label: idCardTypes[key],
    value: key,
  })),
);

export const jewishnessTribesSelector = createSelector([getTribes], (tribes) =>
  Object.keys(tribes).map((key) => ({
    label: tribes[key],
    value: key,
  })),
);

export const jewishnessFilesSelector = createSelector([getSelectedItem], (jewishness) => ({
  personIdCardFile: jewishness?.person?.idCardFile || '',
  personIdCardBackFile: jewishness?.person?.idCardBackFile || '',
  personBirthCertificateFile: jewishness?.person?.birthCertificateFile || '',
  personKetubaFile: jewishness?.person?.ketubaFile || '',
  personMarriageCertificateFile: jewishness?.person?.marriageCertificateFile || '',
  personGetFile: jewishness?.person?.getFile || '',
  spouseIdCardFile: jewishness?.spouse?.idCardFile || '',
  spouseIdCardBackFile: jewishness?.spouse?.idCardBackFile || '',
  personParentsFatherIdCardFile: jewishness?.person?.parents?.father?.idCardFile || '',
  personParentsFatherIdCardBackFile: jewishness?.person?.parents?.father?.idCardBackFile || '',
  personParentsMotherIdCardFile: jewishness?.person?.parents?.mother?.idCardFile || '',
  personParentsMotherIdCardBackFile: jewishness?.person?.parents?.mother?.idCardBackFile || '',
  personParentsKetubaFile: jewishness?.person?.parents?.ketubaFile || '',
  personParentsMarriageCertificateFile: jewishness?.person?.parents?.marriageCertificateFile || '',
  personGrandParentsPaternalKetubaFile: jewishness?.person?.grandParents?.paternalKetubaFile || '',
  personGrandParentsPaternalMarriageCertificateFile:
    jewishness?.person?.grandParents?.paternalMarriageCertificateFile || '',
  personGrandParentsPaternalCemeteryBurialCertificateFile:
    jewishness?.person?.grandParents?.paternalCemeteryBurialCertificateFile || '',
  personGrandParentsMaternalKetubaFile: jewishness?.person?.grandParents?.maternalKetubaFile || '',
  personGrandParentsMaternalMarriageCertificateFile:
    jewishness?.person?.grandParents?.maternalMarriageCertificateFile || '',
  personGrandParentsMaternalCemeteryBurialCertificateFile:
    jewishness?.person?.grandParents?.maternalCemeteryBurialCertificateFile || '',
  personWitnessesIdCardFiles:
    jewishness?.person?.witnesses?.map(({ idCardFile }) => idCardFile) || [],
  personWitnessesIdCardBackFiles:
    jewishness?.person?.witnesses?.map(({ idCardBackFile }) => idCardBackFile) || [],
  personWitnessesKetubaFiles:
    jewishness?.person?.witnesses?.map(({ ketubaFile }) => ketubaFile) || [],
  personWitnessesMarriageCertificateFiles:
    jewishness?.person?.witnesses?.map(({ marriageCertificateFile }) => marriageCertificateFile) ||
    [],
}));
